import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from '@xstate/react';
import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';
import {
  isLoading as isLoadingSelector,
  isSaving as isSavingSelector, contextSelector,
  StepEvents,
  log,
} from '@bingads-webui-campaign-react/step-wizard-machine';
import { useIsMounted } from './use-is-mounted';

/**
 * Loads step's data asynchronously. The returned promise must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property, completely replacing
 * the current value.
 *
 * The function is called when the wizard enters the step.
 * The wizard and the step will be blocked until the promise resolves.
 *
 * If promise is rejected, the step will:
 * - transition to `FAILED_INIT` state if `hasIncomingSystemError` returns true;
 * - transition through `VALIDATING -> (VALID | INVALID)`.
 * @callback loadData
 * @param {Object} context - the current context
 * @returns {Promise<Object>} a promise that must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property, completely replacing
 * the current value.
 */

/**
 * Saves step's data asynchronously. The returned promise must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property, completely replacing
 * the current value.
 *
 * The function is called when the wizard attempts to leave the step.
 * The wizard and the step will be blocked until the promise resolves.
 *
 * If promise is rejected, the step will transition through `VALIDATING -> (VALID | INVALID)`.
 * @callback saveData
 * @param {Object} context - the current context
 * @returns {Promise<Object>} a promise that must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property, completely replacing
 * the current value.
 */


/**
 * Default step repository React component.
 * @param {Object} props the component's props
 * @param {Object} props.stepService an instance of the step's state machine
 * @param {loadData} props.loadData a callback which loads data
 * @param {saveData} props.saveData a callback which saves data
 * @returns {Element} an empty VDOM
 */
export const StepRepository = ({
  stepService,
  loadData,
  saveData,
}) => {
  const isMountedRef = useIsMounted();

  const isLoading = useSelector(stepService, isLoadingSelector);
  const isSaving = useSelector(stepService, isSavingSelector);
  const context = useSelector(stepService, contextSelector);
  const contextRef = useRef(context);
  contextRef.current = context;
  const asyncTokenRef = useRef();

  useEffect(() => {
    if (isLoading) {
      log('StepRepository loading data with loadData');
      if (asyncTokenRef.current) {
        asyncTokenRef.current.cancel();
        asyncTokenRef.current = null;
      }
      asyncTokenRef.current = createAsyncToken(loadData(contextRef.current))
        .on({
          onSuccess: (arg0) => {
            if (isMountedRef.current) {
              log('StepRepository sending DONE_LOADING');
              stepService.send({ type: StepEvents.DONE_LOADING, data: arg0 });
            } else {
              log('StepRepository ignoring sending DONE_LOADING due to unmount');
            }
          },
          onError: (arg0) => {
            if (isMountedRef.current) {
              log('StepRepository sending FAILED_LOADING');
              stepService.send({ type: StepEvents.FAILED_LOADING, data: arg0 });
            } else {
              log('StepRepository ignoring sending FAILED_LOADING due to unmount');
            }
          },
        });
    } else if (isSaving) {
      log('StepRepository saving data with saveData');
      if (asyncTokenRef.current) {
        asyncTokenRef.current.cancel();
        asyncTokenRef.current = null;
      }
      asyncTokenRef.current = createAsyncToken(saveData(contextRef.current))
        .on({
          onSuccess: (arg0) => {
            if (isMountedRef.current) {
              log('StepRepository sending DONE_SAVING');
              stepService.send({ type: StepEvents.DONE_SAVING, data: arg0 });
            } else {
              log('StepRepository ignoring sending DONE_SAVING due to unmount');
            }
          },
          onError: (arg0) => {
            if (isMountedRef.current) {
              log('StepRepository sending FAILED_SAVING');
              stepService.send({ type: StepEvents.FAILED_SAVING, data: arg0 });
            } else {
              log('StepRepository ignoring sending FAILED_SAVING due to unmount');
            }
          },
        });
    }
  }, [stepService, loadData, saveData, isLoading, isSaving, isMountedRef]);

  log('StepRepository.render: isLoading=_, isSaving=_', isLoading, isSaving);

  return null;
};

StepRepository.propTypes = {
  stepService: PropTypes.shape({
    subscribe: PropTypes.func.isRequired,
    send: PropTypes.func,
  }).isRequired,
  loadData: PropTypes.func.isRequired,
  saveData: PropTypes.func.isRequired,
};
